import * as React from "react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Box, Loader, LoadingOverlay, Modal } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateOrder } from "../useCreateOrder";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { FormProvider, useForm } from "react-hook-form";
import { useLazyQuery, useMutation } from "@apollo/client";

import {
  Order,
  OrderComment,
  Product,
  updatePatient,
  updateProduct,
  ImprintChoiceRef,
  OrderTypeChoiceRef,
  MaterialRef,
  AestheticsRef,
  FullDentureRefs,
  Rdv2ChoiceRef,
  JASPER_BOT_USER_GROUP_ID,
  TryInFeedbackRef,
  getLastShippingDateFromDeliveryDate,
  PartialDentureOrderTypeChoiceRef,
  PartialDentureMaterialRef,
  PartialDentureAestheticsRef,
  PartialDentureRefs,
  PartialDentureRdv2ChoiceRef,
  PartialDentureTryInFeedbackRef,
  Rdv2PreviousProducts,
  Stepper,
  mapFieldsToValues,
  stepLabels,
  useWorkflow,
  useWorkflowFieldsAndDefaultValues,
  WorkflowStep,
  useCreateProduct,
  NOTIFY_INTERNAL_PICKUP_REQUIRED,
  UPDATE_DELIVERY_DATE,
  UPDATE_ORDER,
  SEND_MESSAGE,
  HooksAndOptionsMappedValues,
  InformationMappedValues,
  ProsthesisMappedValues,
  PatientFormValues,
  DeliveryDateType,
  ExtendedOrder,
  GetDeliveryDateType,
  ItemMaterials,
  OrderCommentType,
  Rdv2ChoiceType,
  Rdv2ReturnMethod,
  ItemType as SharedItemType,
  RemovableItemOrderType,
  ImprintType,
  ModalOrderConfirmed,
  ModalReturnInstructions,
  CreateRemovableItemFullDentureInfo,
  CreateRemovableItemPartialDentureInfo,
} from "@jasper/shared";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  GetOrderEstimatedDeliveryDateDocument,
  OrderStatus,
} from "/@/generated/graphql";
import { CreateOrderAddProduct } from "../CreateOrderAddProduct";
import { notifications } from "@mantine/notifications";
import { CreateProductSummarySideBar } from "/@/pages/products/create/CreateProductSummarySideBar";
import { useCombinedWorkflowOrderStore } from "/@/shared/stores/orders/create-combined-workflow-store.store";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import { ProductFromGetOrderByUniqueAttributeQuery } from "/@/shared/types/products/products.types";
import { CreateOrderStep } from "/@/shared/types/orders/createOrder.types";

export const SteppedOrderCreationPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuthContext();
  const isRdv2Continuation = state?.isRdv2Continuation ?? false;
  const previousProducts = state?.previousProducts as
    Rdv2PreviousProducts | undefined;

  if (!state || !state.id || !state.workflow) {
    console.error("No state found");
  }

  const [initialCameFromOtherProduct] = useState(() => {
    const stateWorkflowFiltered = (state?.workflow ?? [])
      .map((type: string) => type.split(" ")[0])
      .filter((type: string) =>
        [SharedItemType.PARTIAL_DENTURE, SharedItemType.FULL_DENTURE].includes(
          type as SharedItemType
        )
      );
    return (
      !!state?.comingFrom ||
      !!state?.cameFromOtherProduct ||
      !!state?.isRdv2Continuation ||
      (state?.workflow?.length ?? 0) > stateWorkflowFiltered.length
    );
  });

  const [isShowAddProductModal, setIsShowAddProductModal] = useState(false);
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [isShowReturnInstructionsModal, setIsShowReturnInstructionsModal] =
    useState(false);
  const [isShowInfoModal, setIsShowInfoModal] = useState(false);
  const [confirmedDeliveryDate, setConfirmedDeliveryDate] =
    useState<string>("");
  const hasNavigatedToOldWorkflowRef = useRef(false);

  const { existingDraftOrderLoading, order, refetchOrder } = useCreateOrder(
    state ? { newOrderId: state.id } : undefined
  );

  const filteredWorkflow = useMemo(() => {
    if (isRdv2Continuation) {
      return Object.keys(previousProducts ?? {});
    }

    const workflowFromState = (state?.workflow ?? [])
      .map((type: string) => type.split(" ")[0])
      .filter((type: string) =>
        [SharedItemType.PARTIAL_DENTURE, SharedItemType.FULL_DENTURE].includes(
          type as SharedItemType
        )
      );

    // Fallback: if state.workflow is empty but order.products exists
    if (
      workflowFromState.length === 0 &&
      order?.products &&
      order.products.length > 0
    ) {
      return order.products
        .map((p: Product) => p.productType)
        .filter((type: string) =>
          [
            SharedItemType.PARTIAL_DENTURE,
            SharedItemType.FULL_DENTURE,
          ].includes(type as SharedItemType)
        );
    }

    // Sync with order.products: if products were added via the modal,
    // order.products may have more items than state.workflow
    if (order?.products && order.products.length > 0) {
      const productTypes = order.products
        .map((p: Product) => p.productType)
        .filter((type: string) =>
          [
            SharedItemType.PARTIAL_DENTURE,
            SharedItemType.FULL_DENTURE,
          ].includes(type as SharedItemType)
        );
      if (productTypes.length > workflowFromState.length) {
        return productTypes;
      }
    }

    return workflowFromState;
  }, [state?.workflow, order?.products, isRdv2Continuation, previousProducts]);

  const [getDeliveryDate] = useLazyQuery(GetOrderEstimatedDeliveryDateDocument);
  const [updateDeliveryDate] = useMutation(UPDATE_DELIVERY_DATE);

  const [combinedWorkflowProducts, updateCombinedWorkflowProducts] =
    useCombinedWorkflowOrderStore(state => [
      state.combinedWorkflowProducts,
      state.updateCombinedWorkflowProducts,
    ]);

  const [
    productStep,
    rawCombinedProductIndex,
    updateCombinedProductIndex,
    updateProductStep,
    updateCreateOrderStep,
  ] = useCreateOrderStore(state => [
    state.productNewWFStep,
    state.createProductIndex,
    state.updateCreateProductIndex,
    state.updateProductNewWFStep,
    state.updateCreateOrderStep,
  ]);

  // Use filteredWorkflow when available, otherwise fallback to the store
  // This handles the webapp case where state.workflow can be empty
  // but combinedWorkflowProducts was already set correctly by the form
  const effectiveWorkflow =
    filteredWorkflow.length > 0 ? filteredWorkflow : combinedWorkflowProducts;

  // Clamp the index to be within effectiveWorkflow bounds
  // This prevents out-of-bounds access when the store has an index from CreateOrderPage
  // (e.g. createProductIndex=1 for FULL_DENTURE in order.products, but effectiveWorkflow=["FULL_DENTURE"] has length 1)
  const combinedProductIndex = Math.min(
    rawCombinedProductIndex,
    Math.max(0, effectiveWorkflow.length - 1)
  );

  const [updateOrder, { loading: updateOrderLoading }] =
    useMutation(UPDATE_ORDER);

  const [sendChatMessage] = useMutation(SEND_MESSAGE);
  const [notifyInternalPickupRequired] = useMutation(
    NOTIFY_INTERNAL_PICKUP_REQUIRED
  );

  const { removableItemChoicesData, removableItemChoicesLoading } =
    useCreateProduct(effectiveWorkflow as SharedItemType[]);

  const product = useMemo(() => {
    const targetProductType =
      effectiveWorkflow[combinedProductIndex]?.split(" ")[0];

    if (
      isRdv2Continuation &&
      targetProductType === SharedItemType.FULL_DENTURE &&
      productStep > 0
    ) {
      const fullDentureProducts =
        order?.products?.filter(
          (p: Product) => p.productType === SharedItemType.FULL_DENTURE
        ) ?? [];
      // we take the second full denture product created
      return (
        (fullDentureProducts[
          fullDentureProducts.length - 1
        ] as unknown as Product) ?? {}
      );
    }

    if (
      isRdv2Continuation &&
      targetProductType === SharedItemType.PARTIAL_DENTURE &&
      productStep > 0
    ) {
      const partialDentureProducts =
        order?.products?.filter(
          (p: Product) => p.productType === SharedItemType.PARTIAL_DENTURE
        ) ?? [];
      return (
        (partialDentureProducts[
          partialDentureProducts.length - 1
        ] as unknown as Product) ?? {}
      );
    }

    // Handle multiple PARTIAL_DENTURE products (e.g pap maxillary + pap mandibular in orderRecap)
    if (targetProductType === SharedItemType.PARTIAL_DENTURE) {
      const partialDentureProducts =
        order?.products?.filter(
          (p: Product) => p.productType === SharedItemType.PARTIAL_DENTURE
        ) ?? [];
      if (partialDentureProducts.length > 1) {
        let typeIndex = 0;
        for (let i = 0; i < combinedProductIndex; i++) {
          if (
            effectiveWorkflow[i]?.split(" ")[0] ===
            SharedItemType.PARTIAL_DENTURE
          ) {
            typeIndex++;
          }
        }
        return (partialDentureProducts[typeIndex] as unknown as Product) ?? {};
      }
    }
    const foundProduct = order?.products?.find(
      (p: Product) => p.productType === targetProductType
    );
    return (foundProduct as unknown as Product) ?? {};
  }, [
    order?.products,
    combinedProductIndex,
    effectiveWorkflow,
    isRdv2Continuation,
    productStep,
  ]);

  const fullDentureOrderType = useMemo(() => {
    const removableItem = product?.removableItem?.[0];
    return (
      (removableItem?.fullDentureOrderType as RemovableItemOrderType) ?? null
    );
  }, [
    product?.removableItem?.[0]?.id,
    product?.removableItem?.[0]?.fullDentureOrderType,
  ]);

  const partialDentureOrderType = useMemo(() => {
    const removableItem = product?.removableItem?.[0];
    return (
      (removableItem?.partialDentureOrderType as RemovableItemOrderType) ?? null
    );
  }, [
    product?.removableItem?.[0]?.id,
    product?.removableItem?.[0]?.partialDentureOrderType,
  ]);

  const useNewPapWorkflow =
    useFeatureFlagEnabled("new_pap_workflow_v2") ?? false;
  const pacCarrierIntegration =
    useFeatureFlagEnabled("pac_carrier_integration") ?? false;
  const papCarrierIntegration =
    useFeatureFlagEnabled("pap_carrier_integration") ?? false;

  const partialDentureOrderTypeChoiceRef =
    useRef<PartialDentureOrderTypeChoiceRef>(null);
  const partialDentureMaterialRef = useRef<PartialDentureMaterialRef>(null);
  const partialDentureAestheticsRef = useRef<PartialDentureAestheticsRef>(null);
  const partialDentureRdv2ChoiceRef = useRef<PartialDentureRdv2ChoiceRef>(null);
  const partialDentureTryInFeedbackRef =
    useRef<PartialDentureTryInFeedbackRef>(null);

  const partialDentureRefs: PartialDentureRefs = useMemo(
    () => ({
      orderTypeChoiceRef: partialDentureOrderTypeChoiceRef,
      materialRef: partialDentureMaterialRef,
      aestheticsRef: partialDentureAestheticsRef,
      rdv2ChoiceRef: partialDentureRdv2ChoiceRef,
      tryInFeedbackRef: partialDentureTryInFeedbackRef,
    }),
    []
  );

  const workflow = useMemo(
    () =>
      useWorkflowFieldsAndDefaultValues({
        workflow: effectiveWorkflow,
        order: order ? (order as unknown as Order) : undefined,
        product,
        cameFromOtherProduct: initialCameFromOtherProduct,
        fullDentureOrderType,
        isRdv2Continuation,
        previousProducts,
        useNewPapWorkflow,
        partialDentureRefs,
        partialDentureOrderType,
      }),
    [
      effectiveWorkflow,
      order,
      product,
      combinedProductIndex,
      fullDentureOrderType,
      previousProducts,
      useNewPapWorkflow,
      partialDentureOrderType,
    ]
  );

  const methods = useForm({
    defaultValues: workflow?.workflowDefaultValues[combinedProductIndex] ?? {},
  });
  const navigateToOldWorkflow = () => {
    // Find the next non-PAC/PAP product starting from the current index
    const products = order?.products ?? [];
    const currentProductType =
      effectiveWorkflow[combinedProductIndex]?.split(" ")[0];
    const currentProductInOrder = products.findIndex(
      (p: Product) => p.productType === currentProductType
    );

    let targetIndex = -1;
    for (let i = currentProductInOrder + 1; i < products.length; i++) {
      const p = products[i] as Product;
      if (
        p.productType !== SharedItemType.PARTIAL_DENTURE &&
        p.productType !== SharedItemType.FULL_DENTURE
      ) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex === -1) {
      targetIndex =
        products.findIndex(
          (p: Product) =>
            p.productType !== SharedItemType.PARTIAL_DENTURE &&
            p.productType !== SharedItemType.FULL_DENTURE
        ) ?? 0;
    }

    // Check if this non-PAC/PAP product was already processed
    // If targetIndex <= rawCombinedProductIndex that was set before entering PAC workflow,
    // it means the product was already configured → go to Review
    if (targetIndex < currentProductInOrder) {
      // Product is before the current PAC in order → already processed
      updateCreateOrderStep(CreateOrderStep.Review);
    } else {
      updateCreateOrderStep(CreateOrderStep.Product);
    }

    updateCombinedProductIndex(Math.max(0, targetIndex));
    navigate("/create-order", {
      state: {
        id: state.id,
        comingFrom: "create-new-order",
      },
    });
  };

  const updateOrderWrapper = async (data: { variables: any }) => {
    const result = await updateOrder(data);
    return result.data?.updateOrder as Order;
  };

  const getCommentBlock = (order: ExtendedOrder, orderComment: string) => {
    const result: {
      create?: Array<{ comment: string }>;
      update?: Array<{
        data: { comment: { set: string } };
        where: { id: string };
      }>;
    } = {};

    // Handle order comment (from textarea on recap page)
    if (orderComment) {
      if (isRdv2Continuation) {
        // RDV2+: Always create a new comment to preserve RDV1 comments
        if (!result.create) {
          result.create = [];
        }
        result.create.push({
          comment: orderComment,
        });
      } else {
        // RDV1: Update existing or create new
        const existingOrderComment = (order?.orderComment ?? []).find(
          (comment: OrderComment) => comment.type === OrderCommentType.ORDER
        );

        if (existingOrderComment) {
          result.update = [
            {
              data: {
                comment: {
                  set: orderComment,
                },
              },
              where: {
                id: existingOrderComment.id,
              },
            },
          ];
        } else {
          result.create = [
            {
              comment: orderComment,
            },
          ];
        }
      }
    }

    // Handle dentist notes (try-in feedback for full denture)
    // Only relevant for RDV2+ after a try-in
    if (isRdv2Continuation) {
      const tryInProduct = order?.products?.findLast(
        (p: Product) =>
          p.productType === SharedItemType.FULL_DENTURE &&
          p.removableItem?.[0]?.imprintType === ImprintType.TRY_IN_RDV2
      );

      const dentistNotes = tryInProduct?.removableItem?.[0]?.dentistNotes;

      if (dentistNotes) {
        const tryInFeedbackHeader = "=== Remarques sur l'essayage ===";
        const tryInSection = `${tryInFeedbackHeader}\n${dentistNotes}`;

        if (!result.create) {
          result.create = [];
        }
        result.create.push({
          comment: tryInSection,
        });
      }

      const partialDentureTryInProduct = order?.products?.findLast(
        (p: Product) =>
          p.productType === SharedItemType.PARTIAL_DENTURE &&
          p.removableItem?.[0]?.imprintType == ImprintType.TRY_IN_RDV2
      );

      const partialDentureDentistNotes =
        partialDentureTryInProduct?.removableItem?.[0]?.dentistNotes;

      if (partialDentureDentistNotes) {
        const partialDentureTryInFeedbackHeader =
          "=== Remarques sur l'essayage (PAP) ===";
        const partialDentureTryInSection = `${partialDentureTryInFeedbackHeader}\n${partialDentureDentistNotes}`;

        if (!result.create) {
          result.create = [];
        }
        result.create.push({
          comment: partialDentureTryInSection,
        });
      }
    }

    if (result.create || result.update) {
      return result;
    }
    return undefined;
  };

  const saveOrderRecap = useCallback(
    async (
      orderComment: string,
      deliveryDate?: Date,
      updateStatus: boolean = false,
      setIsShowAddProductModal: boolean = true
    ) => {
      setShouldShowLoader(setIsShowAddProductModal);
      try {
        // freshOrder to get the lastest dentistNotes saved
        const { data: freshOrderData } = await refetchOrder();
        const freshOrder =
          freshOrderData?.getOrderByUniqueAttribute as ExtendedOrder;

        await updateOrder({
          variables: {
            where: {
              id: order?.id,
            },
            data: {
              status: updateStatus
                ? {
                    set: OrderStatus.Confirmed,
                  }
                : undefined,
              comment: {
                set: orderComment ?? null,
              },
              orderBilling:
                updateStatus &&
                !(
                  isRdv2Continuation &&
                  order?.orderBilling &&
                  order.orderBilling.length > 0
                )
                  ? {
                      create: {
                        user: {
                          connect: {
                            id: order?.user?.id,
                          },
                        },
                      },
                    }
                  : undefined,
              confirmedAt: updateStatus
                ? {
                    set: Date.now(),
                  }
                : undefined,
              orderComment: getCommentBlock(freshOrder, orderComment),
              deliveryDate:
                order?.deliveryDate === null && deliveryDate
                  ? {
                      set: deliveryDate,
                    }
                  : undefined,
              lastShippingDate:
                order?.deliveryDate === null && deliveryDate
                  ? {
                      set: getLastShippingDateFromDeliveryDate(
                        new Date(deliveryDate)
                      ),
                    }
                  : undefined,
            },
          },
        });
      } catch (e) {
        console.error(e);
      }
    },
    [
      order?.id,
      order?.user?.id,
      order?.deliveryDate,
      updateOrder,
      setShouldShowLoader,
      order?.orderBilling?.length,
      isRdv2Continuation,
    ]
  );

  const getCurrentPageFormFieldsValues = useCallback(() => {
    const stepFields = workflow.workflowFields[combinedProductIndex];
    const currentField = stepFields[productStep];
    const fields = Object.keys(currentField);
    const values = methods.getValues(fields);
    const mappedValues = mapFieldsToValues(fields, values);
    return { fields, values, mappedValues };
  }, [workflow, combinedProductIndex, productStep, methods]);

  const [workflowToDisplay, setWorkflowToDisplay] = useState<
    WorkflowStep[] | null
  >(null);
  const currentWorkflowRef = useRef<string>("");

  const save = useCallback(
    async (mappedValues: any) => {
      const currentProduct: any = effectiveWorkflow[combinedProductIndex];
      const currentStepLabel =
        workflow.workflowMap?.[combinedProductIndex]?.[productStep]?.label;

      if (currentStepLabel === "Patient") {
        try {
          const result = await updatePatient(
            updateOrderWrapper,
            mappedValues as PatientFormValues,
            state.id
          );
          if (result) {
            await refetchOrder();
            return true;
          }
          return false;
        } catch (e) {
          console.error(e);
          return false;
        }
      } else {
        try {
          const result = await updateProduct(
            updateOrderWrapper,
            mappedValues as
              | InformationMappedValues
              | ProsthesisMappedValues
              | HooksAndOptionsMappedValues,
            state.id,
            currentProduct,
            currentStepLabel,
            product
          );
          if (result) {
            await refetchOrder();
            return true;
          }
          return false;
        } catch (e) {
          console.error(e);
          return false;
        }
      }
    },
    [effectiveWorkflow, combinedProductIndex, productStep, state.id, product]
  );

  const prevStep = useCallback(() => {
    const indexToUse =
      combinedProductIndex < (order?.products?.length ?? 1) - 1
        ? combinedProductIndex - 1
        : combinedProductIndex;
    const values = order?.products?.[indexToUse]?.removableItem?.map(
      item => item.itemMaterial?.id
    );
    if (productStep === 0 && combinedProductIndex > 0) {
      const prevStepValue =
        workflow.workflowMap?.[combinedProductIndex - 1]?.[
          workflow.workflowMap[combinedProductIndex - 1].length - 1
        ];
      const prevIndex =
        prevStepValue?.label === stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS &&
        (values?.length ?? 0) > 0 &&
        values?.every(value => value === ItemMaterials.VALPLAST)
          ? 1
          : 0;
      updateCombinedProductIndex(combinedProductIndex - 1);
      updateProductStep(
        workflow.workflowMap?.[combinedProductIndex - 1].length - prevIndex
      );
      return;
    }
    const prevStepValue =
      workflow.workflowMap?.[combinedProductIndex]?.[productStep - 1];
    if (
      prevStepValue?.label === stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS &&
      (values?.length ?? 0) > 0 &&
      values?.every(value => value === ItemMaterials.VALPLAST)
    ) {
      updateProductStep(productStep - 2);
    } else {
      updateProductStep(productStep - 1);
    }
  }, [
    order,
    product,
    productStep,
    combinedProductIndex,
    workflow,
    updateCombinedProductIndex,
    updateProductStep,
  ]);

  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;

  const [workflowVersion, setWorkflowVersion] = useState(0);

  const imprintChoiceRef = useRef<ImprintChoiceRef>(null);
  const orderTypeChoiceRef = useRef<OrderTypeChoiceRef>(null);
  const materialRef = useRef<MaterialRef>(null);
  const aestheticsRef = useRef<AestheticsRef>(null);
  const rdv2ChoiceRef = useRef<Rdv2ChoiceRef>(null);
  const tryInFeedbackRef = useRef<TryInFeedbackRef>(null);

  const fullDentureRefs: FullDentureRefs = useMemo(
    () => ({
      imprintChoiceRef,
      orderTypeChoiceRef,
      materialRef,
      aestheticsRef,
      rdv2ChoiceRef,
      tryInFeedbackRef,
    }),
    []
  );

  const nextStep = useCallback(async () => {
    const currentProductStep = productStep;

    const { fields, mappedValues } = getCurrentPageFormFieldsValues();

    const isValid = await methods.trigger(fields);

    const haveRecapDisabled =
      effectiveWorkflow.length - 1 > combinedProductIndex;

    const hasNonPacProductsRemaining =
      initialCameFromOtherProduct && !haveRecapDisabled;

    const hasOtherProduct =
      (haveRecapDisabled &&
        effectiveWorkflow[combinedProductIndex + 1] !== undefined &&
        ![SharedItemType.PARTIAL_DENTURE, SharedItemType.FULL_DENTURE].includes(
          effectiveWorkflow[combinedProductIndex + 1] as SharedItemType
        )) ||
      hasNonPacProductsRemaining;

    const nextStepValue =
      workflowRef.current.workflowMap?.[combinedProductIndex]?.[
        currentProductStep + 1
      ];
    const materialFields = fields.filter(field => field.includes("material"));
    const values = methods.getValues(materialFields);
    if (isValid) {
      try {
        let nextPossiblePage =
          haveRecapDisabled || hasNonPacProductsRemaining ? 2 : 1;
        const currentProductType =
          effectiveWorkflow[combinedProductIndex]?.split(" ")[0];

        if (currentProductType === SharedItemType.FULL_DENTURE) {
          const stepLabel =
            workflowRef.current.workflowMap?.[combinedProductIndex]?.[
              productStep
            ]?.label;

          let saved = false;
          if (stepLabel === stepLabels.FULL_DENTURE.CHOICE) {
            await fullDentureRefs.rdv2ChoiceRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.FULL_DENTURE.IMPRINT_CHOICE) {
            await fullDentureRefs.imprintChoiceRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.FULL_DENTURE.ORDER_TYPE) {
            await fullDentureRefs.orderTypeChoiceRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.FULL_DENTURE.MATERIAL) {
            await fullDentureRefs.materialRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.FULL_DENTURE.AESTHETICS) {
            await fullDentureRefs.aestheticsRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.FULL_DENTURE.TRY_IN_FEEDBACK) {
            await fullDentureRefs.tryInFeedbackRef.current?.saveData();
            saved = true;
          }

          if (saved) {
            await refetchOrder();
          }
        }
        if (
          currentProductType === SharedItemType.PARTIAL_DENTURE &&
          useNewPapWorkflow
        ) {
          const stepLabel =
            workflowRef.current.workflowMap?.[combinedProductIndex]?.[
              productStep
            ]?.label;

          let saved = false;
          if (stepLabel === stepLabels.PARTIAL_DENTURE.ORDER_TYPE) {
            await partialDentureRefs.orderTypeChoiceRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.PARTIAL_DENTURE.MATERIAL) {
            await partialDentureRefs.materialRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.PARTIAL_DENTURE.AESTHETICS) {
            await partialDentureRefs.aestheticsRef.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.PARTIAL_DENTURE.CHOICE) {
            await partialDentureRefs.rdv2ChoiceRef?.current?.saveData();
            saved = true;
          } else if (stepLabel === stepLabels.PARTIAL_DENTURE.TRY_IN_FEEDBACK) {
            await partialDentureRefs.tryInFeedbackRef?.current?.saveData();
            saved = true;
          }
          if (saved) {
            await refetchOrder();
          }
        }
        if (
          nextStepValue?.label ===
            stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS &&
          !useNewPapWorkflow &&
          values.every(
            (value: ItemMaterials) => value === ItemMaterials.VALPLAST
          )
        ) {
          nextPossiblePage = nextPossiblePage + 1;
        }
        const result = await save(mappedValues);

        const lastProductStep = isRdv2Continuation
          ? (workflowRef.current.workflowMap?.[combinedProductIndex]?.length ??
              0) - 1
          : workflowRef.current.workflowFields[combinedProductIndex].length -
            nextPossiblePage;

        if (result && currentProductStep < lastProductStep) {
          if (
            nextStepValue?.label ===
              stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS &&
            !useNewPapWorkflow &&
            values.every(
              (value: ItemMaterials) => value === ItemMaterials.VALPLAST
            )
          ) {
            if (hasOtherProduct) {
              navigateToOldWorkflow();
            } else {
              updateProductStep(currentProductStep + 2);
            }
          } else {
            updateProductStep(currentProductStep + 1);
          }
          setWorkflowVersion(prev => prev + 1);
        } else if (hasOtherProduct) {
          updateCombinedProductIndex(combinedProductIndex + 1);
          navigateToOldWorkflow();
        } else if (
          workflowRef.current.workflowMap?.length &&
          workflowRef.current.workflowMap?.length - 1 > combinedProductIndex
        ) {
          updateCombinedProductIndex(combinedProductIndex + 1);
          updateProductStep(0);
          setWorkflowVersion(prev => prev + 1);
        } else {
          notifications.show({
            title: "Erreur",
            message: "Une erreur est survenue lors de la sauvegarde.",
            color: "red",
          });
        }
      } catch (e) {
        if (!(e instanceof Error) || e.message !== "validation_error") {
          console.error(e);
          notifications.show({
            title: "Erreur",
            message: "Une erreur est survenue lors de la sauvegarde.",
            color: "red",
          });
        }
      }
    }
  }, [
    order,
    product,
    productStep,
    combinedProductIndex,
    methods,
    effectiveWorkflow,
    save,
    isRdv2Continuation,
  ]);

  const freshOrder = useMemo(() => {
    if (!order) return null;
    return {
      ...order,
      patient: order.patient ? { ...order.patient } : null,
    };
  }, [order]);

  const workflowStepperUnfiltered = useWorkflow(
    initialCameFromOtherProduct,
    effectiveWorkflow,
    false,
    freshOrder as ExtendedOrder,
    user as any,
    removableItemChoicesData,
    nextStep,
    prevStep,
    product,
    getDeliveryDate as GetDeliveryDateType,
    () => refetchOrder(),
    setIsShowAddProductModal,
    saveOrderRecap,
    updateDeliveryDate as DeliveryDateType,
    workflowVersion,
    fullDentureRefs,
    fullDentureOrderType,
    isRdv2Continuation,
    previousProducts,
    useNewPapWorkflow,
    partialDentureRefs,
    partialDentureOrderType
  );

  const isLoading =
    existingDraftOrderLoading ||
    removableItemChoicesLoading ||
    (updateOrderLoading && shouldShowLoader) ||
    !order;

  const fullDentureProduct = order?.products?.findLast((p: Product) =>
    p.removableItem?.some(item =>
      (item.itemType as string)?.startsWith(SharedItemType.FULL_DENTURE)
    )
  );

  const partialDentureProduct = order?.products?.findLast((p: Product) =>
    p.removableItem?.some(item =>
      (item.itemType as string)?.startsWith(SharedItemType.PARTIAL_DENTURE)
    )
  );

  type OrderProduct = NonNullable<
    NonNullable<typeof order>["products"]
  >[number];

  const rdv2Products: OrderProduct[] = isRdv2Continuation
    ? effectiveWorkflow
        .map((productType: string) =>
          order?.products?.findLast(
            (p: OrderProduct) => p.productType === productType
          )
        )
        .filter((p?: OrderProduct): p is OrderProduct => !!p)
    : [];

  const needsRdv2Info = (product: OrderProduct) => {
    const removableItem = product.removableItem?.[0];
    return (
      removableItem?.returnMethod !== Rdv2ReturnMethod.DIGITAL_IMPRINT &&
      removableItem?.rdv2Choice !== Rdv2ChoiceType.REQUEST_NEW_WAX_BITE
    );
  };

  const needsInternalPickup = (product: OrderProduct) =>
    product.removableItem?.[0]?.returnMethod === Rdv2ReturnMethod.PICKUP &&
    !((product.productType as string) === SharedItemType.PARTIAL_DENTURE
      ? papCarrierIntegration
      : pacCarrierIntegration);

  const targetProduct = isRdv2Continuation
    ? (rdv2Products.find(needsRdv2Info) ?? rdv2Products[0])
    : (fullDentureProduct ?? partialDentureProduct);
  const isPartialDenture = isRdv2Continuation
    ? (targetProduct?.productType as string) === SharedItemType.PARTIAL_DENTURE
    : !fullDentureProduct && !!partialDentureProduct;

  const onSubmit = async (data: any) => {
    if (data.deliveryDate !== undefined) {
      await saveOrderRecap(data.comment, data.deliveryDate, true);
    }

    // The back may have recomputed deliveryDate on RDV2+ continuation.
    // Refetch so the success modal reflects the authoritative DB value.
    const refreshed = await refetchOrder();
    const authoritativeDeliveryDate =
      refreshed?.data?.getOrderByUniqueAttribute?.deliveryDate ??
      methods.getValues("deliveryDate");

    if (authoritativeDeliveryDate) {
      const parsed = new Date(authoritativeDeliveryDate);
      if (!Number.isNaN(parsed.valueOf())) {
        setConfirmedDeliveryDate(
          `Date de livraison estimée: ${parsed.toLocaleDateString("fr-FR")}`
        );
      }
    }
    let showPickupModal = false;

    if (isRdv2Continuation && order?.provider?.id) {
      const messageTexts = new Set(
        rdv2Products.map(product => {
          const rdv2OrderType =
            product.removableItem?.[0]?.fullDentureOrderType ??
            product.removableItem?.[0]?.partialDentureOrderType;

          if (rdv2OrderType === RemovableItemOrderType.FINAL) {
            return "We will send you back some physical elements for finishing";
          }
          if (rdv2OrderType === RemovableItemOrderType.TRY_IN) {
            return "We will send you back some physical elements for set up try in";
          }
          return "";
        })
      );

      for (const messageText of messageTexts) {
        if (!messageText) continue;
        try {
          await sendChatMessage({
            variables: {
              args: {
                order: { connect: { id: order.id } },
                userGroup: { connect: { id: JASPER_BOT_USER_GROUP_ID } },
                message: messageText,
                isNotification: true,
              },
              olderChatRead: true,
              supplier: order.provider.id,
            },
          });
        } catch (error) {
          console.error("Error sending RDV2 chat message:", error);
        }
      }

      showPickupModal = rdv2Products.some(needsInternalPickup);
    }

    try {
      localStorage.removeItem(`orderCreationPage-${state.id}`);
      localStorage.removeItem(`orderCreationProductIndex-${state.id}`);
    } catch (error) {
      console.warn("Could not clean up localStorage:", error);
    }
    setIsShowConfirmModal(true);
    if (showPickupModal && order) {
      try {
        await notifyInternalPickupRequired({
          variables: {
            orderId: order.id,
            createPickup: pacCarrierIntegration,
          },
        });
      } catch (error) {
        console.error("Error sending internal pickup notification:", error);
      }
    }
  };

  useEffect(() => {
    if (order?.id) {
      setWorkflowVersion(prev => prev + 1);
    }
  }, [order?.id, order?.patient?.id]);

  const SUPPORTED_TYPES = [
    SharedItemType.PARTIAL_DENTURE,
    SharedItemType.FULL_DENTURE,
  ];

  // Sync the Zustand store with filteredWorkflow (available immediately from state.workflow)
  // This replaces the old useEffect that waited for order?.products to load
  useEffect(() => {
    // Don't overwrite the store when filteredWorkflow is empty
    if (filteredWorkflow.length === 0) {
      return;
    }

    const hasNonSupportedInStore = combinedWorkflowProducts.some(
      (type: string) =>
        !SUPPORTED_TYPES.includes(type.split(" ")[0] as SharedItemType)
    );

    if (
      hasNonSupportedInStore ||
      combinedWorkflowProducts.length !== filteredWorkflow.length
    ) {
      updateCombinedWorkflowProducts(filteredWorkflow);
    }

    const orderProductsLength = order?.products?.length ?? 0;
    if (
      rawCombinedProductIndex >= filteredWorkflow.length &&
      rawCombinedProductIndex >= orderProductsLength
    ) {
      updateCombinedProductIndex(Math.max(0, filteredWorkflow.length - 1));
    }
  }, [filteredWorkflow, combinedWorkflowProducts, rawCombinedProductIndex]);

  useEffect(() => {
    if (isRdv2Continuation) {
      updateCombinedProductIndex(0);
      updateProductStep(0);
    }
  }, [isRdv2Continuation]);

  useEffect(() => {
    const currentWorkflow = workflowStepperUnfiltered?.[combinedProductIndex];
    const currentProductType = effectiveWorkflow[combinedProductIndex];

    if (combinedProductIndex >= effectiveWorkflow.length) {
      return;
    }

    if (workflowStepperUnfiltered && workflowStepperUnfiltered.length > 0) {
      if (currentWorkflow) {
        const workflowKey = `${combinedProductIndex}-${currentWorkflow.map(step => step.label).join(",")}-${order?.id}-${order?.patient?.id || "no-patient"}-${workflowVersion || 0}`;
        if (workflowKey !== currentWorkflowRef.current) {
          currentWorkflowRef.current = workflowKey;
          setWorkflowToDisplay(currentWorkflow);
        }

        hasNavigatedToOldWorkflowRef.current = false;
      } else if (
        !SUPPORTED_TYPES.includes(
          currentProductType?.split(" ")[0] as SharedItemType
        ) &&
        !hasNavigatedToOldWorkflowRef.current
      ) {
        hasNavigatedToOldWorkflowRef.current = true;
        navigateToOldWorkflow();
      }
    }
  }, [
    workflowStepperUnfiltered,
    order?.id,
    order?.patient?.id,
    workflowVersion,
    combinedProductIndex,
    effectiveWorkflow,
  ]);

  return (
    <Box style={{ height: "calc(100vh - 60px)" }}>
      {isShowConfirmModal && (
        <ModalOrderConfirmed
          isOpen={isShowConfirmModal}
          onClose={() => {
            setIsShowConfirmModal(false);

            if (targetProduct && needsRdv2Info(targetProduct)) {
              setIsShowInfoModal(true);
            } else {
              navigate(`/order/${order?.id}`);
            }
          }}
          title={confirmedDeliveryDate}
        />
      )}
      {isShowReturnInstructionsModal && (
        <ModalReturnInstructions
          isOpen={isShowReturnInstructionsModal}
          onClose={() => {
            setIsShowReturnInstructionsModal(false);
            navigate(`/order/${order?.id}`);
          }}
          title={confirmedDeliveryDate}
        />
      )}
      {isShowInfoModal &&
        order &&
        (() => {
          return (
            <Modal
              size="lg"
              opened={isShowInfoModal}
              onClose={() => {
                setIsShowInfoModal(false);
                navigate(`/order/${order?.id}`);
              }}
            >
              {isPartialDenture ? (
                <CreateRemovableItemPartialDentureInfo
                  product={
                    {
                      ...targetProduct,
                      orderId: order.id,
                    } as ProductFromGetOrderByUniqueAttributeQuery
                  }
                  dentistNotes={targetProduct?.removableItem?.[0]?.dentistNotes}
                  orderComments={
                    order.orderComment
                      ?.filter((c: OrderComment) => c.comment)
                      .map((c: OrderComment) => c.comment)
                      .join("\n") || undefined
                  }
                  orderReference={order.orderReference}
                  orderId={order.id}
                  practitionerName={`Dr. ${order.user?.firstName ?? ""} ${order.user?.lastName ?? ""}`}
                  patientName={`${order.patient?.firstName ?? ""} ${order.patient?.lastName ?? ""}`}
                  clinicCode={order.user?.userGroup?.clinicNumber}
                  pickupCarrier={user?.userGroup?.pickupCarrier}
                />
              ) : (
                <CreateRemovableItemFullDentureInfo
                  product={
                    {
                      ...targetProduct,
                      orderId: order.id,
                    } as ProductFromGetOrderByUniqueAttributeQuery
                  }
                  pickupCarrier={user?.userGroup?.pickupCarrier}
                  dentistNotes={targetProduct?.removableItem?.[0]?.dentistNotes}
                  orderComments={
                    order.orderComment
                      ?.filter((c: OrderComment) => c.comment)
                      .map((c: OrderComment) => c.comment)
                      .join("\n") || undefined
                  }
                  orderReference={order.orderReference}
                  practitionerName={`Dr. ${order.user?.firstName ?? ""} ${order.user?.lastName ?? ""}`}
                  patientName={`${order.patient?.firstName ?? ""} ${order.patient?.lastName ?? ""}`}
                  clinicCode={order.user?.userGroup?.clinicNumber}
                  orderId={order.id}
                />
              )}
            </Modal>
          );
        })()}
      <Modal
        size="100%"
        opened={isShowAddProductModal}
        onClose={() => {
          setIsShowAddProductModal(false);
        }}
      >
        <CreateOrderAddProduct
          closeModal={() => setIsShowAddProductModal(false)}
        />
      </Modal>
      <LoadingOverlay visible={isLoading}>
        <Loader />
      </LoadingOverlay>
      {!isLoading && workflowToDisplay && (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "10fr 2fr",
            height: "100%",
          }}
        >
          <Box p="lg">
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                style={{ height: "100%" }}
              >
                <Stepper
                  key={`${order?.id}-${product.id}-${combinedProductIndex}-${order?.patient?.id || "no-patient"}`}
                  variant="textDown"
                  data={workflowToDisplay}
                  page={productStep}
                  setPage={updateProductStep}
                  isSubmitDisabled={
                    workflowToDisplay?.find(
                      (step: { label: string }) =>
                        step.label === stepLabels.RECAP
                    ) === undefined
                  }
                  productIndex={combinedProductIndex}
                  workflowStepperUnfiltered={workflowStepperUnfiltered}
                  setProductIndex={updateCombinedProductIndex}
                  isLoading={isLoading}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              </form>
            </FormProvider>
          </Box>
          <Box>
            <CreateProductSummarySideBar
              key={`${order?.id}-${productStep}-${combinedProductIndex}-${order?.patient?.id || "no-patient"}`}
              product={product}
              patient={order?.patient}
              order={order as ExtendedOrder}
              isBothJaws={product?.removableItem?.length > 1}
              isRdv2Continuation={isRdv2Continuation}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
